import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { isAccessRowActive } from "@/lib/access";

/**
 * Single source of truth for the user's active certification.
 *
 * Reads `profile.active_certification_id` from `AuthContext` and writes
 * back via Supabase. Server-side trigger enforces that the new id has a
 * non-revoked, non-expired row in `user_certification_access` for
 * non-admins; we mirror that check client-side so a stale row can never
 * be re-activated.
 */
export function useActiveCertification() {
  const { user, profile, refreshProfile, isAdmin } = useAuth();

  const activeCertId = profile?.active_certification_id ?? null;

  const setActiveCertId = useCallback(
    async (id: string | null) => {
      if (!user) return { error: "Not signed in" } as const;
      if (id && !isAdmin) {
        const { data: rows, error: accessErr } = await supabase
          .from("user_certification_access")
          .select("certification_id, expires_at, revoked_at")
          .eq("user_id", user.id)
          .eq("certification_id", id)
          .is("revoked_at", null)
          .limit(1);
        if (accessErr) return { error: accessErr.message } as const;
        const row = rows?.[0];
        if (!row || !isAccessRowActive(row)) {
          return { error: "You no longer have access to this certification" } as const;
        }
      }
      const { error } = await supabase
        .from("profiles")
        .update({ active_certification_id: id })
        .eq("id", user.id);
      if (error) return { error: error.message } as const;
      await refreshProfile();
      return { error: null } as const;
    },
    [user, refreshProfile, isAdmin],
  );

  return { activeCertId, setActiveCertId };
}
